import type { AuditFields, CommonFields } from "./common";
import type { PandaDocConfig } from "./external/pandaDoc";

// ==================== PARTNER ENUMS ====================

export enum PartnerStatus {
  ACTIVE = "active",
  PENDING = "pending",
  SUSPENDED = "suspended",
  TERMINATED = "terminated",
}

// ==================== PARTNER TYPES ====================

/**
 * Partner-specific override for a task type.
 * Stored in Partner.Configuration["task_types"] keyed by task type constant.
 */
export interface TaskTypeConfig {
  enabled?: boolean;
  threshold_override?: string;
  due_days_override?: number;
  show_on_exhibit?: boolean;
  signature?: SignatureTaskConfig;
}

export interface SignatureTaskConfig {
  signature_template?: string;
  enabled?: boolean;
}

export type TaskTypesConfig = Record<string, TaskTypeConfig>;

/**
 * Typed partner configuration map
 */
export interface PartnerConfiguration extends JSONMap {
  branding?: PartnerBrandingConfig;
  pandadoc?: PandaDocConfig;
  task_types?: TaskTypesConfig;
}

/**
 * Generic JSON configuration map
 */
export type JSONMap = Record<string, unknown>;

/**
 * Branding configuration stored in configuration.branding
 */
export interface PartnerBrandingConfig {
  logo_image?: string | null;
  logo_image_dark?: string | null;
  brand_primary?: string;
  brand_secondary?: string;
  brand_primary_dark?: string;
  brand_secondary_dark?: string;
}

/**
 * Base partner interface - fields for both create and read
 */
interface PartnerBase {
  accel_product_name?: string;
  partner_company_name?: string;
  partner_code?: string;
  domain?: string;
  configuration?: PartnerConfiguration;
  /**
   * Canonical partner branding (colors), from the dedicated partners.branding column
   * (ADO #4116 / #5360). Prefer this over configuration.branding, which is being
   * retired (#4758).
   */
  branding?: PartnerBrandingConfig;
  logo_image?: string;
  status?: PartnerStatus;
}

/**
 * Full partner record from the API - includes server-managed fields
 */
export interface Partner extends PartnerBase, CommonFields, AuditFields {}

/**
 * Payload for creating a new partner
 * Required fields: partner_company_name, partner_code (4 chars), domain
 */
export interface PartnerCreate extends PartnerBase {
  partner_company_name: string;
  partner_code: string;
  domain: string;
}

/**
 * Payload for updating a partner - all fields optional except id
 */
export interface PartnerUpdate extends Partial<PartnerBase> {
  id: string;
}

// ==================== HELPER TYPES ====================

/**
 * Partner configuration with typed PandaDoc config
 * Use when you know the configuration contains pandadoc settings
 */
export interface PartnerConfigurationWithPandaDoc extends JSONMap {
  pandadoc?: PandaDocConfig;
}

/**
 * Type guard to check if configuration has PandaDoc settings
 */
export const hasPandaDocConfig = (config?: JSONMap): config is PartnerConfigurationWithPandaDoc => {
  return config !== undefined && "pandadoc" in config;
};

/**
 * Extract PandaDoc config from partner configuration
 * Returns undefined if not configured
 */
export const extractPandaDocConfig = (configuration?: JSONMap): PandaDocConfig | undefined => {
  if (!hasPandaDocConfig(configuration)) {
    return undefined;
  }
  return configuration.pandadoc;
};
